import { useEffect, useState } from "react";
import { endpoints } from "@/lib/api";
import { Target, RefreshCw, CheckSquare, Square, Activity } from "lucide-react";
import { toast } from "sonner";

export const SovereignPace = () => {
    const [strikes, setStrikes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [pulse, setPulse] = useState({ total_contributed_usd: 0, ticker: [], fund_name: "CyberKids Fund" });

    const loadStrikes = async () => {
        setLoading(true);
        try {
            const res = await endpoints.strikesToday();
            setStrikes(res.data.items || []);
        } catch (e) {
            toast.error("Failed to load strike quota");
        } finally {
            setLoading(false);
        }
    };

    const loadPulse = async () => {
        try {
            const res = await endpoints.legacyPulse();
            setPulse(res.data);
        } catch (e) {
            // silent — ticker fallback
        }
    };

    useEffect(() => {
        loadStrikes();
        loadPulse();
        // Refresh pulse every 5 minutes — ticker is decorative, no need to hammer the endpoint.
        const id = setInterval(loadPulse, 300000);
        return () => clearInterval(id);
    }, []);

    const toggleStrike = async (strike) => {
        const next = !strike.completed;
        setStrikes((prev) => prev.map((s) => (s.id === strike.id ? { ...s, completed: next } : s)));
        try {
            await endpoints.strikeUpdate(strike.id, next);
            if (next) toast.success(`STRIKE LOGGED · ${strike.label}`);
        } catch (e) {
            toast.error("Failed to update strike");
            loadStrikes();
        }
    };

    const reset = async () => {
        try {
            const res = await endpoints.strikesReset();
            setStrikes(res.data.items || []);
            toast.success("Daily quota reset");
        } catch (e) {
            toast.error("Reset failed");
        }
    };

    const completed = strikes.filter((s) => s.completed).length;
    const total = strikes.length || 6;
    const pct = Math.round((completed / total) * 100);

    return (
        <section
            id="sovereign-pace"
            data-testid="sovereign-pace"
            className="relative py-20 border-b border-zinc-900"
        >
            <div className="max-w-[1600px] mx-auto px-6 md:px-8">
                <SectionHeader
                    eyebrow="MODULE · 00X"
                    title="Sovereign Pace Tracker"
                    subtitle="Daily transition tempo. The metronome for the operator."
                />

                <div className="grid grid-cols-12 gap-4 lg:gap-6 mt-10">
                    {/* DAILY STRIKE QUOTA */}
                    <div
                        data-testid="daily-strike-quota"
                        className="col-span-12 lg:col-span-7 relative border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 corner-bracket"
                    >
                        <div className="flex items-start justify-between flex-wrap gap-4">
                            <div>
                                <div className="flex items-center gap-2">
                                    <Target className="w-4 h-4 text-gold-300" />
                                    <span className="label-tactical">DAILY STRIKE QUOTA</span>
                                </div>
                                <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-2">
                                    5 Broker Scout Signals · 1 Vault Check-In
                                </h3>
                            </div>
                            <button
                                data-testid="strikes-reset-btn"
                                onClick={reset}
                                className="border border-zinc-800 hover:border-gold-300 text-zinc-400 hover:text-gold-300 px-3 py-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] font-bold transition-colors"
                            >
                                <RefreshCw className="w-3 h-3" />
                                Reset
                            </button>
                        </div>

                        {/* Progress bar */}
                        <div className="mt-6">
                            <div className="flex items-center justify-between mb-2">
                                <span className="badge-tier text-zinc-500">
                                    PACE · {completed}/{total}
                                </span>
                                <span
                                    data-testid="strike-progress-pct"
                                    className="badge-tier text-gold-300"
                                >
                                    {pct}%
                                </span>
                            </div>
                            <div className="relative h-2 bg-zinc-900 border border-zinc-800">
                                <div
                                    className="absolute top-0 left-0 h-full bg-gold-300 transition-all"
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                        </div>

                        {/* Strike list */}
                        <div className="mt-6 grid gap-2">
                            {loading ? (
                                <div className="text-zinc-500 text-sm">Loading quota...</div>
                            ) : (
                                strikes.map((s, i) => (
                                    <button
                                        key={s.id}
                                        data-testid={`strike-${i}`}
                                        onClick={() => toggleStrike(s)}
                                        className={`group flex items-center gap-4 border ${
                                            s.completed
                                                ? "border-gold-300/40 bg-gold-300/5"
                                                : "border-zinc-800/80 hover:border-gold-300/60"
                                        } px-4 py-3 transition-all text-left`}
                                    >
                                        <div className="text-gold-300">
                                            {s.completed ? (
                                                <CheckSquare className="w-5 h-5" />
                                            ) : (
                                                <Square className="w-5 h-5 text-zinc-600 group-hover:text-gold-300" />
                                            )}
                                        </div>
                                        <div className="flex-1">
                                            <div
                                                className={`text-sm font-bold uppercase tracking-wide ${
                                                    s.completed ? "text-gold-300 line-through" : "text-white"
                                                }`}
                                            >
                                                {s.label}
                                            </div>
                                            <div className="badge-tier text-zinc-500 mt-1">
                                                {s.category === "broker_scout" ? "BROKER · SCOUT SIGNAL" : "VAULT · CHECK-IN"}
                                            </div>
                                        </div>
                                        <div
                                            className={`badge-tier px-2 py-1 border ${
                                                s.completed
                                                    ? "border-gold-300/60 text-gold-300"
                                                    : "border-zinc-700 text-zinc-500"
                                            }`}
                                        >
                                            {s.completed ? "STRUCK" : "PENDING"}
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* LEGACY PULSE TICKER */}
                    <div
                        data-testid="legacy-pulse"
                        className="col-span-12 lg:col-span-5 relative border border-zinc-800/80 bg-zinc-950/60 p-6 lg:p-8 corner-bracket"
                    >
                        <div className="flex items-center gap-2">
                            <Activity className="w-4 h-4 text-gold-300 animate-pulse" />
                            <span className="label-tactical">LEGACY PULSE · LIVE</span>
                        </div>
                        <h3 className="text-2xl lg:text-3xl font-black uppercase tracking-tight text-white mt-2">
                            CyberKids Fund
                        </h3>
                        <p className="text-zinc-400 text-sm mt-2">
                            10% sovereign tax on every hull revenue & strike earning.
                        </p>

                        <div className="mt-6 border border-gold-300/20 bg-gold-300/5 p-5">
                            <div className="label-tactical">TOTAL CONTRIBUTED · USD</div>
                            <div
                                data-testid="legacy-total"
                                className="text-4xl lg:text-5xl font-black text-gold-300 tracking-tight mt-1"
                            >
                                ${pulse.total_contributed_usd?.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                            </div>
                            <div className="badge-tier text-zinc-500 mt-2">
                                FUND · {pulse.fund_name?.toUpperCase()}
                            </div>
                        </div>

                        <div className="mt-6 overflow-hidden border-y border-zinc-800/80 py-3 relative">
                            <div className="flex gap-12 whitespace-nowrap animate-ticker">
                                {[...(pulse.ticker || []), ...(pulse.ticker || [])].map((t, i) => (
                                    <div
                                        key={i}
                                        className="flex items-center gap-3 text-xs uppercase tracking-[0.18em] font-bold"
                                    >
                                        <span className="w-1.5 h-1.5 bg-gold-300" />
                                        <span className="text-gold-300">+${t.amount_usd?.toFixed(2)}</span>
                                        <span className="text-zinc-500">·</span>
                                        <span className="text-zinc-300">{t.source}</span>
                                        {t.note && <span className="text-zinc-500">— {t.note}</span>}
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="mt-4 grid grid-cols-2 gap-3">
                            <MiniMetric label="Tax Rate" value="10%" />
                            <MiniMetric label="Entries" value={String(pulse.ticker?.length || 0)} />
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

const MiniMetric = ({ label, value }) => (
    <div className="border border-zinc-800/80 px-4 py-3">
        <div className="badge-tier text-zinc-500">{label.toUpperCase()}</div>
        <div className="text-xl font-black text-white mt-1">{value}</div>
    </div>
);

export const SectionHeader = ({ eyebrow, title, subtitle }) => (
    <div className="flex items-end justify-between flex-wrap gap-4 border-b border-zinc-800/80 pb-6">
        <div>
            <div className="flex items-center gap-3">
                <span className="w-6 h-px bg-gold-300" />
                <span className="label-tactical">{eyebrow}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white mt-3">
                {title}
            </h2>
            {subtitle && <p className="text-zinc-400 text-sm sm:text-base mt-2 max-w-2xl">{subtitle}</p>}
        </div>
    </div>
);

export default SovereignPace;
